import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { EyeOff, Layers } from 'lucide-react';

import type { BookingEventDetail } from '@/types';

import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { bookingTimeRange } from '@/helpers';

import { DISPLAY_DATES_FORMAT } from '@/constants/dates';

const Row = (props: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
    <span className="text-muted-foreground">{props.label}</span>
    <span className="min-w-0">{props.children}</span>
  </div>
);

type EventSummaryProps = {
  event: BookingEventDetail;
};

const eventSpan = (event: BookingEventDetail) => {
  const startDay = format(parseISO(event.starts_at), DISPLAY_DATES_FORMAT);
  const endDay = format(parseISO(event.ends_at), DISPLAY_DATES_FORMAT);

  if (startDay === endDay) {
    return `${startDay}, ${bookingTimeRange(event.starts_at, event.ends_at)}`;
  }

  return `${startDay} – ${endDay}`;
};

export const EventSummary = (props: EventSummaryProps) => {
  const { event } = props;
  const { t } = useTranslation();

  const statusLabel = () => {
    if (event.status === 'cancelled') {
      return t('event_cancelled');
    }
    if (event.completed) {
      return t('event_completed');
    }
    return t('event_scheduled');
  };

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge
          variant={event.status === 'cancelled' ? 'destructive' : 'secondary'}
        >
          {statusLabel()}
        </Badge>

        {event.split_into_waves && (
          <Badge
            variant="outline"
            className="gap-1"
          >
            <Layers className="size-3" />
            {t('Split into waves')}
          </Badge>
        )}

        {!event.online_bookable && (
          <Badge
            variant="outline"
            className="gap-1"
          >
            <EyeOff className="size-3" />
            {t('Hidden from online booking')}
          </Badge>
        )}
      </div>

      <Separator className="my-2" />

      <Row label={t('Event')}>{event.name}</Row>
      <Row label={t('Location')}>{event.location.name}</Row>
      <Row label={t('Date')}>{eventSpan(event)}</Row>

      {!!event.service && <Row label={t('Service')}>{event.service.name}</Row>}

      {!event.split_into_waves && (
        <Row label={t('Spots')}>
          {`${event.taken_spots}/${event.capacity ?? 0} · ${t('Free spots')}: ${event.free_spots ?? 0}`}
        </Row>
      )}

      {event.split_into_waves && (
        <Row label={t('Registrations')}>{event.taken_spots}</Row>
      )}
    </div>
  );
};
